import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { stripeApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, DollarSign, Users, CheckCircle, AlertCircle, XCircle, Pencil, TrendingUp, Search,
} from 'lucide-react';

/**
 * Platform-wide subscription management — only accessible to super_admin.
 *
 * Shows MRR/ARR across every organisation, a searchable table of all orgs
 * with their current plan/status, and an "Override" dialog that lets the
 * platform admin flip an organisation's plan, mark it as trialing/comp'd, or
 * cancel it — without going through Stripe Checkout.  All changes are
 * recorded in `subscription_audit` on the backend for compliance.
 */
export default function PlatformSubscriptionsPage() {
  const [overview, setOverview] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [o, p] = await Promise.all([
        api.get('/admin/subscriptions/overview'),
        stripeApi.plans().catch(() => ({ data: { plans: [] } })),
      ]);
      setOverview(o.data);
      setPlans(p.data.plans || []);
    } catch (e) {
      toast.error('Failed to load platform subscriptions');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = (overview?.organizations || []).filter(o => {
    if (statusFilter !== 'all' && o.subscription_status !== statusFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return o.organization_name.toLowerCase().includes(q) || o.organization_id.includes(q);
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="platform-subscriptions-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Subscriptions</h1>
        <p className="text-sm text-[#64748B]">Monitor and manage every organization&apos;s Stripe subscription from one place.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="platform-subscriptions-kpis">
        <KpiCard icon={<DollarSign className="w-4 h-4 text-[#166534]" />} label="MRR" value={`$${(overview?.totals?.mrr || 0).toLocaleString()}`} testId="kpi-mrr" />
        <KpiCard icon={<TrendingUp className="w-4 h-4 text-[#0E7490]" />} label="ARR" value={`$${(overview?.totals?.arr || 0).toLocaleString()}`} testId="kpi-arr" />
        <KpiCard icon={<CheckCircle className="w-4 h-4 text-[#166534]" />} label="Active" value={overview?.totals?.active || 0} testId="kpi-active" />
        <KpiCard icon={<AlertCircle className="w-4 h-4 text-[#CA8A04]" />} label="Trialing" value={overview?.totals?.trialing || 0} testId="kpi-trialing" />
        <KpiCard icon={<Users className="w-4 h-4 text-[#64748B]" />} label="Total orgs" value={overview?.totals?.orgs || 0} testId="kpi-orgs" />
      </div>

      {/* Filter bar */}
      <Card className="careiq-card">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94A3B8]" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by organization name…"
              className="pl-8 h-9 text-sm"
              data-testid="subs-filter-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="subs-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="trialing">Trialing</SelectItem>
              <SelectItem value="canceled">Canceled</SelectItem>
              <SelectItem value="none">No subscription</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="careiq-card">
        <CardHeader><CardTitle className="text-lg text-[#0A2342]">Organizations ({filtered.length})</CardTitle></CardHeader>
        <CardContent className="px-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="subs-table">
              <thead>
                <tr className="text-xs text-[#64748B] border-b border-[#E2E8F0] bg-[#F8FAFC]">
                  <th className="text-left px-4 py-2">Organization</th>
                  <th className="text-left px-2 py-2">Plan</th>
                  <th className="text-left px-2 py-2">Cycle</th>
                  <th className="text-left px-2 py-2">Status</th>
                  <th className="text-right px-2 py-2">MRR</th>
                  <th className="text-right px-2 py-2">Txns</th>
                  <th className="text-left px-2 py-2">Activated</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-[#94A3B8] text-sm">No organizations match your filter.</td></tr>
                )}
                {filtered.map((org) => (
                  <tr key={org.organization_id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`subs-row-${org.organization_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#0A2342]">{org.organization_name}</div>
                      <div className="text-[10px] text-[#94A3B8] font-mono">{org.organization_id}</div>
                    </td>
                    <td className="px-2 py-3 text-[#0A2342] capitalize">{org.plan_name || org.plan_id || <span className="text-[#94A3B8]">—</span>}</td>
                    <td className="px-2 py-3 text-[#64748B] capitalize">{org.billing_cycle || '—'}</td>
                    <td className="px-2 py-3"><StatusChip status={org.subscription_status} /></td>
                    <td className="px-2 py-3 text-right font-medium text-[#0A2342]">${org.mrr.toLocaleString()}</td>
                    <td className="px-2 py-3 text-right text-[#64748B]">{org.transaction_count}</td>
                    <td className="px-2 py-3 text-xs text-[#64748B]">{org.activated_at ? new Date(org.activated_at).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(org)}
                        data-testid={`subs-override-btn-${org.organization_id}`}
                        className="h-7 text-xs border-[#0E7490] text-[#0E7490]"
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Override
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <OverrideDialog
        org={editing}
        plans={plans}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function KpiCard({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#64748B] font-medium mb-1">
        {icon}
        {label}
      </div>
      <div className="text-xl font-semibold text-[#0A2342]">{value}</div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    active: { bg: 'bg-[#166534]/10', text: 'text-[#166534]', icon: <CheckCircle className="w-3 h-3" />, label: 'Active' },
    trialing: { bg: 'bg-[#CA8A04]/10', text: 'text-[#92400E]', icon: <AlertCircle className="w-3 h-3" />, label: 'Trialing' },
    canceled: { bg: 'bg-[#991B1B]/10', text: 'text-[#991B1B]', icon: <XCircle className="w-3 h-3" />, label: 'Canceled' },
    none: { bg: 'bg-[#F1F5F9]', text: 'text-[#64748B]', icon: null, label: '—' },
  };
  const c = map[status] || map.none;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${c.bg} ${c.text}`}>
      {c.icon}
      {c.label}
    </span>
  );
}

function OverrideDialog({ org, plans, onClose, onSaved }) {
  const [planId, setPlanId] = useState('');
  const [cycle, setCycle] = useState('monthly');
  const [status, setStatus] = useState('active');
  const [comp, setComp] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!org) return;
    setPlanId(org.plan_id || plans[0]?.id || '');
    setCycle(org.billing_cycle || 'monthly');
    setStatus(org.subscription_status && ['active', 'trialing', 'canceled'].includes(org.subscription_status) ? org.subscription_status : 'active');
    setComp(false);
  }, [org, plans]);

  if (!org) return null;

  const handleSave = async () => {
    if (!planId) { toast.error('Please pick a plan'); return; }
    setSaving(true);
    try {
      await api.post(`/admin/subscriptions/${org.organization_id}/override`, { plan_id: planId, billing_cycle: cycle, status, comp });
      toast.success(`Subscription updated for ${org.organization_name}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!org} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="override-subscription-dialog">
        <DialogHeader>
          <DialogTitle>Override subscription</DialogTitle>
          <DialogDescription>
            Manually set the plan, cycle, and status for <strong>{org.organization_name}</strong>. This bypasses Stripe Checkout — use for comped accounts, migrations, or remediation. Every change is audit-logged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Plan</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger data-testid="override-plan-select"><SelectValue placeholder="Pick a plan" /></SelectTrigger>
              <SelectContent>
                {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.price}/mo</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Billing cycle</Label>
              <Select value={cycle} onValueChange={setCycle}>
                <SelectTrigger data-testid="override-cycle-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="override-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={comp}
              onChange={(e) => setComp(e.target.checked)}
              className="rounded"
              data-testid="override-comp-checkbox"
            />
            <span>Mark as comped (free) — no Stripe charge will be created</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="careiq-btn-primary" data-testid="override-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
